import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { trackFormSubmit } from "@/utils/tracking";
import { storeUTMParams, useUTMParams } from "@/hooks/useUTMParams";
import { PhoneVerification } from "@/components/admin/PhoneVerification";

// Vehicle type icons
import CoupeIcon from "@/assets/vehicle-icons/coupe.png";
import HatchbackIcon from "@/assets/vehicle-icons/hatchback.png";
import SedanIcon from "@/assets/vehicle-icons/sedan.png";
import MinivanIcon from "@/assets/vehicle-icons/minivan.png";
import SuvIcon from "@/assets/vehicle-icons/suv.png";
import TruckIcon from "@/assets/vehicle-icons/truck.png";

const vehicleTypes = [
  { id: "coupe", name: "Coupe", icon: CoupeIcon },
  { id: "hatchback", name: "Hatchback", icon: HatchbackIcon },
  { id: "sedan", name: "Sedan", icon: SedanIcon },
  { id: "minivan", name: "Minivan", icon: MinivanIcon },
  { id: "suv", name: "SUV & Crossover", icon: SuvIcon },
  { id: "truck", name: "Truck", icon: TruckIcon },
];

const budgetOptions = [
  "Under $200 / Bi-Weekly",
  "$200 - $249 / Bi-Weekly",
  "$250 - $300 / Bi-Weekly",
  "Over $300 / Bi-Weekly",
];

const tradeInOptions = ["Yes", "No", "Unsure"];

const creditRatingOptions = [
  { label: "Excellent (760–900)", value: "excellent" },
  { label: "Very Good (725–759)", value: "very-good" },
  { label: "Good (660–724)", value: "good" },
  { label: "Fair (600–659)", value: "fair" },
  { label: "Poor (300–599)", value: "poor" },
  { label: "No Credit / Unsure", value: "no-credit" },
];

const employmentOptions = [
  "Employed",
  "Self-Employed",
  "Student",
  "Retired / Pension",
  "Other",
];

const incomeTypeOptions = [
  "I know my annual salary",
  "I know my hourly wage",
  "I know my monthly income",
  "Other",
];

interface FormData {
  vehicleType: string;
  budget: string;
  tradeIn: string;
  creditRating: string;
  employmentStatus: string;
  incomeType: string;
  annualIncome: string;
  hourlyWage: string;
  hoursPerWeek: string;
  monthlyIncome: string;
  employerName: string;
  employerPhone: string;
  jobTitle: string;
  yearsEmployed: string;
  address: string;
  yearsAtAddress: string;
  monthsAtAddress: string;
  rentOrOwn: string;
  monthlyHousePayment: string;
  dob: string;
  age: number | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  sin: string;
}

const TOTAL_STEPS = 13;
const STORAGE_KEY = "car_street_lead_v1";

const PreApproval = () => {
  const navigate = useNavigate();
  const utm = useUTMParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    vehicleType: "",
    budget: "",
    tradeIn: "",
    creditRating: "",
    employmentStatus: "",
    incomeType: "",
    annualIncome: "",
    hourlyWage: "",
    hoursPerWeek: "",
    monthlyIncome: "",
    employerName: "",
    employerPhone: "",
    jobTitle: "",
    yearsEmployed: "",
    address: "",
    yearsAtAddress: "",
    monthsAtAddress: "",
    rentOrOwn: "",
    monthlyHousePayment: "",
    dob: "",
    age: null,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    sin: "",
  });

  // Load saved progress from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Only restore if not on success screen
        if (parsed.currentStep < 14) {
          setFormData(parsed.formData || formData);
          setCurrentStep(parsed.currentStep || 1);
        }
      } catch (e) {
        console.error("Failed to load saved progress", e);
      }
    }
  }, []);

  // Save progress to localStorage (except success screen)
  // SECURITY: Never persist SIN or other highly sensitive PII to localStorage
  useEffect(() => {
    if (currentStep < 14) {
      const { sin, ...safeFormData } = formData;
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ formData: safeFormData, currentStep })
      );
    }
  }, [formData, currentStep]);

  const progress = (currentStep / TOTAL_STEPS) * 100;

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelection = (field: keyof FormData, value: any) => {
    updateFormData(field, value);
    setTimeout(() => {
      if (validateStepField(field, value)) {
        setDirection(1);
        setCurrentStep(currentStep + 1);
      }
    }, 400);
  };

  const validateStepField = (field: keyof FormData, value: any): boolean => {
    return value !== "";
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [currentStep]);

  const goToStep = (step: number, dir: number) => {
    setDirection(dir);
    setCurrentStep(step);
  };

  const nextStep = () => {
    if (validateStep()) {
      goToStep(currentStep + 1, 1);
    }
  };

  const prevStep = () => {
    goToStep(currentStep - 1, -1);
  };

  const validateStep = (): boolean => {
    switch (currentStep) {
      case 1:
        if (!formData.vehicleType) {
          toast.error("Please select a vehicle type");
          return false;
        }
        break;
      case 2:
        if (!formData.budget) {
          toast.error("Please select your budget");
          return false;
        }
        break;
      case 3:
        if (!formData.tradeIn) {
          toast.error("Please select if you have a trade-in");
          return false;
        }
        break;
      case 4:
        if (!formData.creditRating) {
          toast.error("Please select your credit rating");
          return false;
        }
        break;
      case 5:
        if (!formData.employmentStatus) {
          toast.error("Please select your employment status");
          return false;
        }
        break;
      case 6:
        if (!formData.incomeType) {
          toast.error("Please select how you know your income");
          return false;
        }
        break;
      case 7:
        if (formData.incomeType === "I know my annual salary" && !formData.annualIncome) {
          toast.error("Please enter your annual income");
          return false;
        }
        if (formData.incomeType === "I know my hourly wage" && (!formData.hourlyWage || !formData.hoursPerWeek)) {
          toast.error("Please enter your hourly wage and hours per week");
          return false;
        }
        if ((formData.incomeType === "I know my monthly income" || formData.incomeType === "Other") && !formData.monthlyIncome) {
          toast.error("Please enter your monthly income");
          return false;
        }
        break;
      case 8:
        if (!formData.employerName || !formData.jobTitle) {
          toast.error("Please enter your employer details");
          return false;
        }
        break;
        if (formData.employerPhone && !isValidPhone(formData.employerPhone)) {
          toast.error("Employer phone must be exactly 10 digits");
          return false;
        }
        break;
      case 9:
        if (!formData.address) {
          toast.error("Please enter your address");
          return false;
        }
        break;
      case 10: {
        const years = parseInt(formData.yearsAtAddress) || 0;
        const months = parseInt(formData.monthsAtAddress) || 0;
        if (years === 0 && months === 0) {
          toast.error("Please enter how long you've been at this address");
          return false;
        }
        if (months > 11) {
          toast.error("Months must be between 0 and 11");
          return false;
        }
        break;
      }
      case 11:
        if (!formData.rentOrOwn || !formData.monthlyHousePayment) {
          toast.error("Please complete all housing information");
          return false;
        }
        break;
      case 12:
        if (!formData.dob || !formData.age) {
          toast.error("Please enter your date of birth");
          return false;
        }
        if (formData.age < 17 || formData.age > 100) {
          toast.error("You must be between 17 and 100 years old");
          return false;
        }
        break;
      case 13:
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
          toast.error("Please complete all required fields");
          return false;
        }
        if (!isValidEmail(formData.email)) {
          toast.error("Please enter a valid email address");
          return false;
        }
        if (!isPhoneVerified) {
          toast.error("Please verify your phone number before submitting");
          return false;
        }
        if (formData.sin && formData.sin.length !== 9) {
          toast.error("SIN must be exactly 9 digits");
          return false;
        }
        break;
    }
    return true;
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone: string): boolean => {
    const cleaned = phone.replace(/\D/g, "");
    return cleaned.length === 10;
  };

  const formatCurrency = (value: string): string => {
    const num = value.replace(/\D/g, "");
    return num ? parseInt(num).toLocaleString() : "";
  };

  const handleCurrencyInput = (field: keyof FormData, value: string) => {
    const num = value.replace(/\D/g, "");
    updateFormData(field, num);
  };

  const calculateAge = (dob: string): number | null => {
    const cleaned = dob.replace(/\D/g, "");
    if (cleaned.length !== 8) return null;
    const day = parseInt(cleaned.substring(0, 2));
    const month = parseInt(cleaned.substring(2, 4));
    const year = parseInt(cleaned.substring(4, 8));
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDobDisplay = (value: string): string => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
  };

  const handleDobChange = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    const limited = cleaned.substring(0, 8);
    updateFormData("dob", limited);
    if (limited.length === 8) {
      const age = calculateAge(limited);
      updateFormData("age", age);
    } else {
      updateFormData("age", null);
    }
  };

  const resetForm = () => {
    setFormData({
      vehicleType: "", budget: "", tradeIn: "", creditRating: "", employmentStatus: "",
      incomeType: "", annualIncome: "", hourlyWage: "", hoursPerWeek: "", monthlyIncome: "",
      employerName: "", employerPhone: "", jobTitle: "", yearsEmployed: "", address: "",
      yearsAtAddress: "", monthsAtAddress: "", rentOrOwn: "", monthlyHousePayment: "",
      dob: "", age: null, firstName: "", lastName: "", email: "", phone: "", sin: "",
    });
    setCurrentStep(1);
    setDirection(1);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    try {
      // SECURITY: Build submission payload with SIN only for secure DB storage,
      // never include raw SIN in email notifications
      const detailedInfo = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        vehicleType: formData.vehicleType,
        budget: formData.budget,
        tradeIn: formData.tradeIn,
        creditRating: formData.creditRating,
        employmentStatus: formData.employmentStatus,
        incomeDetails: {
          type: formData.incomeType,
          annualIncome: formData.annualIncome,
          hourlyWage: formData.hourlyWage,
          hoursPerWeek: formData.hoursPerWeek,
          monthlyIncome: formData.monthlyIncome,
        },
        employerDetails: {
          name: formData.employerName,
          phone: formData.employerPhone,
          jobTitle: formData.jobTitle,
          yearsEmployed: formData.yearsEmployed,
        },
        address: formData.address,
        timeAtAddress: { years: formData.yearsAtAddress, months: formData.monthsAtAddress },
        housing: { rentOrOwn: formData.rentOrOwn, monthlyPayment: formData.monthlyHousePayment },
        dateOfBirth: formData.dob,
        age: formData.age,
        sin: formData.sin ? '***PROVIDED***' : '',
      };

      // Full info with SIN goes only to the secure database, never to email
      const dbDetailedInfo = { ...detailedInfo, sin: formData.sin };

      const { error } = await supabase.rpc("submit_contact_form", {
        p_name: `${formData.firstName} ${formData.lastName}`,
        p_email: formData.email,
        p_phone: formData.phone,
        p_message: `Pre-Approval Application\n\nVehicle Type: ${formData.vehicleType}\nBudget: ${formData.budget}\nTrade-In: ${formData.tradeIn}\nCredit Rating: ${formData.creditRating}`,
        p_notes: JSON.stringify(dbDetailedInfo, null, 2),
      });

      if (error) throw error;

      storeUTMParams(utm);

      // Admin will handle CRM sync manually in Leads Management dashboard

      // Send email notification
      try {
        await supabase.functions.invoke("send-email", {
          body: {
            type: "pre-approval",
            data: detailedInfo,
          },
        });
      } catch (emailError) {
        console.error("Failed to send email notifcation", emailError);
        // Don't block success flow if email fails
      }

      // Track successful form submission
      trackFormSubmit('Pre-Approval', formData.vehicleType);

      // Clear localStorage after successful submission
      localStorage.removeItem(STORAGE_KEY);

      setCurrentStep(14);
      window.scrollTo({ top: 0, behavior: "instant" });

      toast.success("Application submitted! We'll review and get back to you soon.");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Submission failed. Please try again or contact us directly.");
    }
  };

  // Fade animation instead of slide
  const fadeVariants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const fadeTransition = {
    duration: 0.3,
    ease: "easeInOut" as const,
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <SEO
        title="Get Pre-Approved now | Good, Bad or no Credit Financing"
        description="Apply for car financing online. Bad credit? No credit? We can help you get approved for your dream car."
        url="https://carstreet.ca/pre-approval"
      />

      <main className="flex-1 py-4 md:py-8">
        <div className="max-w-[700px] mx-auto px-4">
          {/* Progress Bar */}
          {currentStep <= TOTAL_STEPS && (
            <motion.div className="mb-6" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">Step {currentStep} of {TOTAL_STEPS}</span>
                <span className="text-sm text-muted-foreground">Estimated time: {Math.max(1, Math.ceil((TOTAL_STEPS - currentStep) * 0.5))} min</span>
              </div>
              <Progress value={progress} className="h-2" />
            </motion.div>
          )}

          {/* Form Steps */}
          <div className="bg-card rounded-lg shadow-lg p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                variants={fadeVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={fadeTransition}
              >
                {/* Step 1: Vehicle Type */}
                {currentStep === 1 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">What type of vehicle are you interested in?</h2>
                    <p className="text-muted-foreground mb-6">Select the vehicle type that best suits your needs</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {vehicleTypes.map((type) => (
                        <motion.button
                          key={type.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSelection("vehicleType", type.id)}
                          className={`p-4 rounded-lg border-2 transition-all ${formData.vehicleType === type.id
                            ? "border-primary bg-primary/10 shadow-lg"
                            : "border-border hover:border-primary/50"
                            }`}
                        >
                          <img src={type.icon} alt={type.name} className="w-full h-16 mx-auto mb-2 object-contain" />
                          <div className="font-medium text-sm">{type.name}</div>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Budget */}
                {currentStep === 2 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">What is your budget?</h2>
                    <p className="text-muted-foreground mb-6">Select your preferred bi-weekly payment range</p>
                    <div className="grid grid-cols-2 gap-3">
                      {budgetOptions.map((option) => (
                        <motion.button
                          key={option}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSelection("budget", option)}
                          className={`p-4 rounded-lg border-2 text-center transition-all ${formData.budget === option
                            ? "border-primary bg-primary/10 shadow-md"
                            : "border-border hover:border-primary/50"
                            }`}
                        >
                          <span className="font-medium text-sm">{option}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3: Trade-in */}
                {currentStep === 3 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Do you have a trade-in?</h2>
                    <p className="text-muted-foreground mb-6">Let us know if you have a vehicle to trade in</p>
                    <div className="grid grid-cols-3 gap-3">
                      {tradeInOptions.map((option) => (
                        <motion.button
                          key={option}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSelection("tradeIn", option)}
                          className={`p-4 rounded-lg border-2 text-center transition-all ${formData.tradeIn === option
                            ? "border-primary bg-primary/10 shadow-md"
                            : "border-border hover:border-primary/50"
                            }`}
                        >
                          <span className="font-medium">{option}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 4: Credit Rating */}
                {currentStep === 4 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Estimated credit rating</h2>
                    <p className="text-muted-foreground mb-6">This helps us find the best financing options for you</p>
                    <div className="grid grid-cols-2 gap-3">
                      {creditRatingOptions.map((option) => (
                        <motion.button
                          key={option.value}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSelection("creditRating", option.value)}
                          className={`p-4 rounded-lg border-2 text-center transition-all ${formData.creditRating === option.value
                            ? "border-primary bg-primary/10 shadow-md"
                            : "border-border hover:border-primary/50"
                            }`}
                        >
                          <span className="font-medium text-sm">{option.label}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 5: Employment Status */}
                {currentStep === 5 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Employment Status</h2>
                    <p className="text-muted-foreground mb-6">What is your current employment situation?</p>
                    <div className="grid grid-cols-2 gap-3">
                      {employmentOptions.map((option) => (
                        <motion.button
                          key={option}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSelection("employmentStatus", option)}
                          className={`p-4 rounded-lg border-2 text-center transition-all ${formData.employmentStatus === option
                            ? "border-primary bg-primary/10 shadow-md"
                            : "border-border hover:border-primary/50"
                            }`}
                        >
                          <span className="font-medium text-sm">{option}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 6: Income Type Selection */}
                {currentStep === 6 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Income Details</h2>
                    <p className="text-muted-foreground mb-6">How would you like to provide your income information?</p>
                    <div className="grid grid-cols-2 gap-3">
                      {incomeTypeOptions.map((option) => (
                        <motion.button
                          key={option}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSelection("incomeType", option)}
                          className={`p-4 rounded-lg border-2 text-center transition-all ${formData.incomeType === option
                            ? "border-primary bg-primary/10 shadow-md"
                            : "border-border hover:border-primary/50"
                            }`}
                        >
                          <span className="font-medium text-sm">{option}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 7: Income Input */}
                {currentStep === 7 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Income Information</h2>
                    <p className="text-muted-foreground mb-6">Please provide your income details</p>
                    <div className="space-y-4">
                      {formData.incomeType === "I know my annual salary" && (
                        <div>
                          <Label htmlFor="annualIncome">What is your annual income before taxes & deductions?</Label>
                          <div className="relative mt-2">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                              id="annualIncome"
                              type="text"
                              inputMode="numeric"
                              placeholder="0"
                              value={formatCurrency(formData.annualIncome)}
                              onChange={(e) => handleCurrencyInput("annualIncome", e.target.value)}
                              className="pl-7"
                            />
                          </div>
                        </div>
                      )}
                      {formData.incomeType === "I know my hourly wage" && (
                        <>
                          <div>
                            <Label htmlFor="hourlyWage">How much do you get paid per hour?</Label>
                            <div className="relative mt-2">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                              <Input
                                id="hourlyWage"
                                type="text"
                                inputMode="numeric"
                                placeholder="0"
                                value={formatCurrency(formData.hourlyWage)}
                                onChange={(e) => handleCurrencyInput("hourlyWage", e.target.value)}
                                className="pl-7"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="hoursPerWeek">Average hours per week</Label>
                            <Input
                              id="hoursPerWeek"
                              type="text"
                              inputMode="numeric"
                              placeholder="40"
                              value={formData.hoursPerWeek}
                              onChange={(e) => updateFormData("hoursPerWeek", e.target.value.replace(/\D/g, ""))}
                              className="mt-2"
                            />
                          </div>
                        </>
                      )}
                      {(formData.incomeType === "I know my monthly income" || formData.incomeType === "Other") && (
                        <div>
                          <Label htmlFor="monthlyIncome">What is your monthly income before taxes & deductions?</Label>
                          <div className="relative mt-2">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                              id="monthlyIncome"
                              type="text"
                              inputMode="numeric"
                              placeholder="0"
                              value={formatCurrency(formData.monthlyIncome)}
                              onChange={(e) => handleCurrencyInput("monthlyIncome", e.target.value)}
                              className="pl-7"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 8: Employer Details */}
                {currentStep === 8 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Employer Details</h2>
                    <p className="text-muted-foreground mb-6">Please provide information about your current employer</p>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="employerName">Employer Name *</Label>
                        <Input
                          id="employerName"
                          type="text"
                          placeholder="Company Name"
                          value={formData.employerName}
                          onChange={(e) => updateFormData("employerName", e.target.value)}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="jobTitle">Job Title *</Label>
                        <Input
                          id="jobTitle"
                          type="text"
                          placeholder="Your Position"
                          value={formData.jobTitle}
                          onChange={(e) => updateFormData("jobTitle", e.target.value)}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="employerPhone">Employer Phone (Optional)</Label>
                        <Input
                          id="employerPhone"
                          type="tel"
                          placeholder="(555) 555-1234"
                          value={formData.employerPhone}
                          onChange={(e) => updateFormData("employerPhone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="yearsEmployed">Years Employed (Optional)</Label>
                        <Input
                          id="yearsEmployed"
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          value={formData.yearsEmployed}
                          onChange={(e) => updateFormData("yearsEmployed", e.target.value.replace(/\D/g, ""))}
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 9: Address */}
                {currentStep === 9 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Current Address</h2>
                    <p className="text-muted-foreground mb-6">Please enter your current address in Canada</p>
                    <div>
                      <Label htmlFor="address">Full Address</Label>
                      <Input
                        id="address"
                        type="text"
                        placeholder="123 Main St, City, Province, Postal Code"
                        value={formData.address}
                        onChange={(e) => updateFormData("address", e.target.value)}
                        className="mt-2"
                      />
                    </div>
                  </div>
                )}

                {/* Step 10: Time at Address */}
                {currentStep === 10 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">How long at this address?</h2>
                    <p className="text-muted-foreground mb-6">Let us know how long you've lived at your current address</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="years">Years</Label>
                        <Input
                          id="years"
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          value={formData.yearsAtAddress}
                          onChange={(e) => updateFormData("yearsAtAddress", e.target.value.replace(/\D/g, ""))}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="months">Months</Label>
                        <Input
                          id="months"
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          value={formData.monthsAtAddress}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "");
                            if (parseInt(val) <= 11 || val === "") {
                              updateFormData("monthsAtAddress", val);
                            }
                          }}
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 11: Rent or Own */}
                {currentStep === 11 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Do you Rent or Own?</h2>
                    <p className="text-muted-foreground mb-6">Tell us about your housing situation</p>
                    <div className="space-y-4">
                      <RadioGroup
                        value={formData.rentOrOwn}
                        onValueChange={(value) => updateFormData("rentOrOwn", value)}
                      >
                        <div className="flex items-center space-x-3 p-3 rounded-lg border-2 border-border">
                          <RadioGroupItem value="rent" id="rent" />
                          <Label htmlFor="rent" className="cursor-pointer">Rent</Label>
                        </div>
                        <div className="flex items-center space-x-3 p-3 rounded-lg border-2 border-border">
                          <RadioGroupItem value="own" id="own" />
                          <Label htmlFor="own" className="cursor-pointer">Own</Label>
                        </div>
                      </RadioGroup>
                      {formData.rentOrOwn && (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                          <Label htmlFor="monthlyPayment">
                            Monthly {formData.rentOrOwn === "rent" ? "Rent" : "Mortgage"} Payment
                          </Label>
                          <div className="relative mt-2">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                              id="monthlyPayment"
                              type="text"
                              inputMode="numeric"
                              placeholder="0"
                              value={formatCurrency(formData.monthlyHousePayment)}
                              onChange={(e) => handleCurrencyInput("monthlyHousePayment", e.target.value)}
                              className="pl-7"
                            />
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 12: Date of Birth */}
                {currentStep === 12 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Date of Birth</h2>
                    <p className="text-muted-foreground mb-6">Enter your date of birth (DD/MM/YYYY)</p>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="dob">Date of Birth</Label>
                        <Input
                          id="dob"
                          type="text"
                          inputMode="numeric"
                          placeholder="DD/MM/YYYY"
                          maxLength={10}
                          value={formatDobDisplay(formData.dob)}
                          onChange={(e) => handleDobChange(e.target.value)}
                          className="mt-2"
                        />
                      </div>
                      {formData.age !== null && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`p-3 rounded-lg ${formData.age >= 17 && formData.age <= 100
                            ? "bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900"
                            : "bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900"
                            }`}
                        >
                          <p className="text-sm">
                            Based on your input, you are <strong>{formData.age}</strong> years old.
                            {formData.age >= 17 && formData.age <= 100 ? " If this is correct, press continue." : " Age must be between 17 and 100."}
                          </p>
                        </motion.div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 13: Final Details */}
                {currentStep === 13 && (
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Almost Done!</h2>
                    <p className="text-muted-foreground mb-6">Just a few more details to complete your application</p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="firstName">First Name *</Label>
                          <Input
                            id="firstName"
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => updateFormData("firstName", e.target.value)}
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="lastName">Last Name *</Label>
                          <Input
                            id="lastName"
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => updateFormData("lastName", e.target.value)}
                            className="mt-2"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => updateFormData("email", e.target.value)}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <PhoneVerification
                          phone={formData.phone}
                          onPhoneChange={(p) => updateFormData("phone", p)}
                          onVerified={setIsPhoneVerified}
                          isVerified={isPhoneVerified}
                        />
                      </div>
                      <div>
                        <Label htmlFor="sin">Social Insurance Number (SIN) <span className="text-muted-foreground font-normal">(optional)</span></Label>
                        <Input
                          id="sin"
                          type="text"
                          inputMode="numeric"
                          placeholder="XXX-XXX-XXX"
                          maxLength={9}
                          value={formData.sin}
                          onChange={(e) => updateFormData("sin", e.target.value.replace(/\D/g, "").slice(0, 9))}
                          className="mt-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Providing your SIN helps us verify your identity and credit history more accurately.
                        </p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg text-xs">
                        <p className="font-medium mb-1">Important Disclosure:</p>
                        <p className="text-muted-foreground leading-relaxed">
                          By submitting this application, you authorize Car Street and its financing partners to obtain your credit report and verify the information provided. This may result in a credit inquiry that could affect your credit score. You understand that submission does not guarantee approval and that all information provided is subject to verification.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Success Screen */}
                {currentStep === 14 && (
                  <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-8">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring" }}
                      className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4"
                    >
                      <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </motion.div>
                    <h2 className="text-2xl font-bold mb-3">Application Submitted!</h2>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Thank you for your application. Our finance team will review your information and contact you within 24 hours to discuss your pre-approval options.
                    </p>
                    <Button size="lg" onClick={() => { resetForm(); navigate("/"); }}>
                      Return to Home
                    </Button>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            {currentStep <= TOTAL_STEPS && (
              <motion.div
                className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-between mt-6 pt-4 border-t"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Button variant="outline" size="lg" onClick={prevStep} disabled={currentStep === 1} className="w-full sm:w-auto">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                {currentStep >= 7 && (
                  currentStep === TOTAL_STEPS ? (
                    <Button onClick={handleSubmit} size="lg" className="w-full sm:w-auto" disabled={!isPhoneVerified || !formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)}>Submit Application</Button>
                  ) : (
                    <Button onClick={nextStep} size="lg" className="w-full sm:w-auto">
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )
                )}
              </motion.div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PreApproval;